import { ActivatedRoute, Router } from '@angular/router';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
  ChangeDetectionStrategy,
  inject,
  forwardRef,
} from '@angular/core';
import {
  EditorFormEntity,
  FormConfiguration,
  FormMode,
} from 'src/app/core/models/form.models';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NomLanguages, NomRoles, NomTimezones } from 'src/app/core/constants';
import {
  UserAccessLevel,
  UserService,
} from 'src/app/core/services/user.service';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/core/state/app.state';
import { AuditTrailComponent } from 'src/app/shared/components/audit-trail/audit-trail.component';
import { BreadcrumbService } from 'src/app/core/services/breadcrumb.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralFormEditorComponent } from 'src/app/shared/components/general-form-editor/general-form-editor.component';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { User } from 'src/app/core/models/core.models';
import { UserActivityLogsComponent } from '../user-activity-logs/user-activity-logs.component';
import { UserLinkedAccountsComponent } from 'src/app/admin/components/user-linked-accounts/user-linked-accounts.component';
import { UserOutputLogsComponent } from '../../../admin/pages/user-output-logs/user-output-logs.component';
import { UserPermissionsComponent } from 'src/app/shared/components/user-edit/user-permissions/user-permissions.component';
import { UserSearchLogsComponent } from '../../../admin/pages/user-search-logs/user-search-logs.component';
import { injectManageUserMutation } from 'src/app/api/queries/user.queries';
import { FormSaveResult } from 'src/app/core/models/form.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import {
  MoveUserCompanyDialogComponent,
  MoveUserCompanyDialogData,
  MoveUserCompanyDialogOutput,
} from 'src/app/admin/dialogs/move-user-company-dialog/move-user-company-dialog.component';

import { Observable, map, of, tap } from 'rxjs';
import { NgClass, NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { SearchInputComponent } from '../search-input/search-input.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../loading/loading.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-editor-user',
  templateUrl: '../general-form-editor/general-form-editor.component.html',
  styleUrl: './editor-user.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    NgStyle,
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    SearchInputComponent,
    FaIconComponent,
    LoadingComponent,
    SuiButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class EditorUserComponent
  extends GeneralFormEditorComponent
  implements AfterViewInit
{
  @Input() user: User;
  @Input() companyId: string | null = null;
  @Input() isPopup: boolean;
  @Output() titleChange = new EventEmitter<string>();

  editorFormEntityName = EditorFormEntity.User;

  private manageUserMutation = injectManageUserMutation();
  private readonly dialogService = inject(DialogService);

  /** The dynamic form registers a control for every item, the action button included. */
  private static readonly MOVE_COMPANY_ACTION_PROPERTY = 'move_company_action';

  constructor(
    protected formBuilder: FormBuilder,
    protected formService: FormsService,
    protected nomService: NomenclatureService,
    protected toastService: ToastService,
    protected userService: UserService,
    protected store: Store<AppState>,
    protected router: Router,
    protected logger: LoggerService,
    protected activatedRoute: ActivatedRoute,
    protected breadcrumbService: BreadcrumbService,
    protected viewContainerRef: ViewContainerRef,
  ) {
    super(
      formBuilder,
      formService,
      toastService,
      store,
      logger,
      router,
      activatedRoute,
      breadcrumbService,
      viewContainerRef,
    );
  }
  initalizeDefaultFormValues(): void {
    this.formGroup.patchValue({
      list_company_objects: false,
      company_id: this.companyId,
      roles: [NomRoles.DEFAULT_USER_ROLE_ID],
      'timezone.id': NomTimezones.EUROPE_SOFIA,
      'language.id': NomLanguages.ENGLISH,
      'user_access_level.id': UserAccessLevel.CLIENT_USER,
    });
  }
  ngOnInit(): void {
    if (this.user) {
      this.formMode = FormMode.Edit;
    }
    super.ngOnInit();

    this.isPopupEditor = this.isPopup;

    if (this.user) {
      this.initializeFormControls(this.user);
      const transformedData = this.transformUserData(this.user);

      this.formService.populateFormValues(this.formGroup, transformedData);

      this.filterTabs();

      this.validateAndHandleInvalidForm();
    } else {
      this.initalizeDefaultFormValues();
    }

    // * Always set up validation sync - but it only shows errors when validationEnabled is true
    this.setupValidationSync();

    // * listen for Form value changes
    this.formGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((values) => values.display_name || ''),
        tap((displayName) => {
          if (this.isPopup) {
            this.titleChange.emit(displayName);
          }
        }),
      )
      .subscribe();
  }

  private initializeFormControls(user: User): void {
    // Check if the FormGroup has already been initialized
    if (this.formGroup) {
      // Check if the 'id' control already exists
      if (this.formGroup.contains('id')) {
        // Update the existing control value
        this.formGroup.get('id').setValue(user.id);
      } else {
        // Add the 'id' control if it doesn't exist
        this.formGroup.addControl('id', this.formBuilder.control(user.id));
      }
    } else {
      // If FormGroup hasn't been initialized, create it with the 'id' control
      this.formGroup = this.formBuilder.group({
        id: this.formBuilder.control(user.id),
      });
    }
  }

  private transformUserData(user: User): any {
    return {
      ...user,
      groups: user.groups.map((group) => group.id),
      roles: user.roles.map((role) => role.id),
      companies: user.companies.map((company) => company.id),
      push_notification_language:
        user?.push_services?.[1]?.language?.id ?? null,
      email_notification_language:
        user?.push_services?.[2]?.language?.id ?? null,
      avatar_base64: user.avatar_url, // to be compatible with image cropper
    };
  }

  getUserIdInputProvider = (): Observable<{ userId: string }> => {
    return of(this.user).pipe(map((user) => ({ userId: user?.id })));
  };

  getUserInputProvider = (): Observable<{ user: User }> => {
    return of({ user: this.user });
  };

  userGroupsSuggestion(): Observable<any> {
    const userId = this.user ? this.user.id : null;
    return this.userService.fetchUserGroups(userId, '').pipe(
      map((groups) =>
        groups.map((group) => ({
          text: group.title,
          id: group.id,
        })),
      ),
    );
  }

  userRolesSuggestion(): Observable<any> {
    return this.userService.fetchUserRoles(this.user?.id, '').pipe(
      map((roles) =>
        roles.map((role) => ({
          text: role.title,
          id: role.id,
        })),
      ),
    );
  }

  userCompaniesSuggestions(): Observable<any> {
    return this.userService.fetchUserCompanies(this.user?.id, '', true).pipe(
      map((companies) =>
        companies.map((company) => ({
          text: company.name,
          id: company.id,
        })),
      ),
    );
  }

  // The base skips these once the editor is destroyed, which is what keeps the
  // dialog host from navigating to /admin/home after its optimistic close.
  onObjectCreated(object: any): void {
    this.router.navigate(['/admin', 'home']);
  }

  onObjectUpdated(object: any): void {
    this.router.navigate(['/admin', 'home']);
  }

  protected override saveViaMutation(
    formData: any,
  ): Promise<FormSaveResult> | null {
    const userData = this.buildUserData(formData);
    const isCreated = !this.user;

    // The admin Users grid mirrors this write from the mutation cache; nothing to
    // broadcast from here.
    return this.manageUserMutation
      .mutateAsync({ userData, isCreate: isCreated })
      .then((user) => ({ object: user, isCreated }));
  }

  private buildUserData(formData: any) {
    const {
      [EditorUserComponent.MOVE_COMPANY_ACTION_PROPERTY]: _moveAction,
      ...userFormData
    } = formData;
    return {
      ...userFormData,
      roles: formData.roles.map((role: any) => {
        return { id: role };
      }),
      groups: formData.groups?.map((group: any) => {
        return { id: group };
      }),
      push_services: {
        1: {
          id: 1,
          language: {
            id: formData.push_notification_language,
          },
        },
        2: {
          id: 2,
          language: {
            id: formData.email_notification_language,
          },
        },
      },

      language: {
        id: formData['language.id'],
      },

      timezone: {
        id: formData['timezone.id'],
      },

      user_access_level: {
        id: formData['user_access_level.id'],
      },

      companies: formData.companies?.map((company: any) => {
        return { id: company };
      }),
    };
  }

  private openMoveCompanyDialog(): void {
    const data: MoveUserCompanyDialogData = { user: this.user };
    const dialogRef = this.dialogService.openSimpleDialog<
      MoveUserCompanyDialogComponent,
      MoveUserCompanyDialogOutput
    >(MoveUserCompanyDialogComponent, {
      data,
      closeOnClick: false,
      closeOnEscape: true,
      // room for the 240px company list inside the pane (the panel clips fixed descendants)
      minHeight: 540,
    });

    dialogRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: DialogCloseOptions<MoveUserCompanyDialogOutput>) => {
        if (result.reason === DialogCloseReason.Save && result.data) {
          this.applyMovedUser(result.data.user);
        }
      });
  }

  /** The move re-read the user; repaint the form so the company shows its new value. */
  private applyMovedUser(user: User): void {
    this.user = user;
    this.formService.populateFormValues(
      this.formGroup,
      this.transformUserData(user),
    );
    if (this.isPopup) {
      this.titleChange.emit(user.display_name);
    }
  }

  onCancel() {
    // !this.isPopup
    //   ? this.router.navigate(['/admin', 'home'])
    //   : this.popupSercvice.closeAllPopups();
  }

  // Override the abstract method from the parent to provide form configuration
  loadFormConfig(): FormConfiguration {
    return {
      tabs: [
        // General Settings Tab
        {
          label: this.translateService.instant(
            'editor-user.label.general-settings',
          ),
          key: 'general-settings',
          icon: ['fas', 'cog'],
          panels: [
            {
              panelName: 'General Settings Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'company_id',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.company',
                  ),
                  controlType: 'select',
                  validators: [Validators.required],
                  // Create offers active companies only (WAP-1701); Edit keeps
                  // the disabled select resolving the user's current company.
                  valueOptionsAsync: this.userService
                    .fetchUserCompanies(
                      '',
                      '',
                      true,
                      true,
                      this.formMode === FormMode.Create,
                    )
                    .pipe(
                      map((companies) => {
                        let options = this.formService.transformForSelect(
                          companies,
                          (company) => company.id,
                          (company) => company.name,
                          null,
                        );
                        return options;
                      }),
                    ),
                  additionalConfig: {
                    disabled: this.formMode === FormMode.Edit,
                    placeholder: this.translateService.instant(
                      'editor-user.select.select-company',
                    ),
                  },
                },
                {
                  // A company change never goes through manage_user (the backend
                  // refuses it); a Global Company Manager moves the user with the
                  // dedicated plan / execute flow (WAP-1702).
                  propertyName:
                    EditorUserComponent.MOVE_COMPANY_ACTION_PROPERTY,
                  propertyLabel: this.translateService.instant(
                    'editor-user.button.move-to-another-company',
                  ),
                  controlType: 'button',
                  availableInFormModes: [FormMode.Edit],
                  userPermission: AppFeatures.GLOBAL_COMPANY_MANAGER_ROOT,
                  action: () => this.openMoveCompanyDialog(),
                  additionalConfig: {
                    variant: 'secondary',
                    size: 'sm',
                    leadingIcon: ['fas', 'exchange-alt'],
                  },
                },
                {
                  propertyName: 'avatar_base64',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.avatar',
                  ),
                  controlType: 'image-cropper',
                  additionalConfig: {
                    defaultImageUrl: '/assets/default-avatar.png',
                  },
                },
                {
                  propertyName: 'display_name',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.name',
                  ),
                  controlType: 'text',
                  validators: [Validators.required],
                },
                {
                  propertyName: 'user_access_level.id',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.user-access-level',
                  ),
                  controlType: 'select',
                  validators: [Validators.required],
                  userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-user.select.select-user-access-level',
                    ),
                  },
                  valueOptionsAsync: this.nomService
                    .fetchUserAccessLevels()
                    .pipe(
                      map((accessLevels) => {
                        let options = this.formService.transformForSelect(
                          accessLevels,
                          (accessLevel) => accessLevel.id,
                          (accessLevel) => accessLevel.title,
                          null,
                        );
                        return options;
                      }),
                    ),
                },

                {
                  propertyName: 'description',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.description',
                  ),
                  controlType: 'textarea',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-user.placeholder.description',
                    ),
                    rows: 4,
                  },
                },
                {
                  propertyName: 'email',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.email',
                  ),
                  controlType: 'text',
                  validators: [Validators.required, Validators.email],
                },
                {
                  propertyName: 'email_alerts',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.alerts-email',
                  ),
                  controlType: 'text',
                  validators: [Validators.email],
                },
                {
                  propertyName: 'list_company_objects',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.view-all-company-objects',
                  ),
                  controlType: 'toggle',
                  additionalConfig: {
                    size: 'small',
                  },
                  defaultValue: false,
                  userPermission:
                    AppFeatures.COMPANY_MANAGER_COMPANY_OBJECTS_TREE,
                  userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        // Language and Region Tab
        {
          label: this.translateService.instant(
            'editor-user.label.language-and-region',
          ),
          key: 'language-and-region',
          icon: ['fas', 'globe'],
          panels: [
            {
              panelName: 'Language And Region Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'timezone.id',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.timezone',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-user.select.select-timezone',
                    ),
                  },
                  valueOptionsAsync: this.nomService.fetchTimezones().pipe(
                    map((timezones) => {
                      let options = this.formService.transformForSelect(
                        timezones,
                        (tz) => tz.id,
                        (tz) => tz.name,
                        null,
                      );
                      return options;
                    }),
                  ),
                  validators: [Validators.required],
                },
                {
                  propertyName: 'language.id',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.language',
                  ),
                  controlType: 'select',
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-user.select.select-language',
                    ),
                  },
                  valueOptionsAsync: this.nomService.fetchLanguages().pipe(
                    map((languages) => {
                      let options = this.formService.transformForSelect(
                        languages,
                        (lang) => lang.id,
                        (lang) => lang.name,
                        null,
                      );
                      return options;
                    }),
                  ),
                  validators: [Validators.required],
                },
                {
                  propertyName: 'push_notification_language',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.push-notification-language',
                  ),
                  controlType: 'select',
                  // Optional: the user may return to "no language" (null)
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-user.select.select-language',
                    ),
                    clearable: true,
                  },
                  valueOptionsAsync: this.nomService.fetchLanguages().pipe(
                    map((languages) => {
                      let options = this.formService.transformForSelect(
                        languages,
                        (lang) => lang.id,
                        (lang) => lang.name,
                        null,
                      );
                      return options;
                    }),
                  ),
                },
                {
                  propertyName: 'email_notification_language',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.email-notification-language',
                  ),
                  controlType: 'select',
                  // Optional: the user may return to "no language" (null)
                  additionalConfig: {
                    placeholder: this.translateService.instant(
                      'editor-user.select.select-language',
                    ),
                    clearable: true,
                  },
                  valueOptionsAsync: this.nomService.fetchLanguages().pipe(
                    map((languages) => {
                      let options = this.formService.transformForSelect(
                        languages,
                        (lang) => lang.id,
                        (lang) => lang.name,
                        null,
                      );
                      return options;
                    }),
                  ),
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        // Groups & Roles Tab
        {
          label: this.translateService.instant(
            'editor-user.label.groups-&-roles',
          ),
          key: 'groups-and-roles',
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          icon: ['fas', 'users'],
          panels: [
            {
              panelName: 'Groups & Roles Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'groups',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.user-groups',
                  ),
                  controlType: 'tag-input',
                  additionalConfig: {
                    valueOptionsAsync: this.userGroupsSuggestion(),
                    disabled:
                      this.currentUser?.feature_components[
                        AppFeatures.COMPANY_MANAGER_COMPANY_GROUP_MANAGEMENT
                      ] !== true &&
                      this.currentUser?.feature_components[
                        AppFeatures.GLOBAL_COMPANY_MANAGER_ROOT
                      ] !== true,
                  },
                  validators: [],
                },
                {
                  propertyName: 'roles',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.user-roles',
                  ),
                  controlType: 'tag-input',
                  additionalConfig: {
                    valueOptionsAsync: this.userRolesSuggestion(),
                    disabled:
                      this.currentUser?.feature_components[
                        AppFeatures.GLOBAL_COMPANY_MANAGER_ROOT
                      ] !== true,
                  },
                  validators: [Validators.required],
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        // Permissions Tab
        {
          label: this.translateService.instant('editor-user.label.permissions'),
          key: 'permissions',
          icon: ['fas', 'lock'],
          availableInFormModes: [FormMode.Edit],
          formPanelsClass: 'no-y-scroll', // added and applied on the container div with all panels
          panels: [
            {
              panelName: 'Permissions Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'component',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserPermissionsComponent,
                    inputProviderAsync: this.getUserInputProvider.bind(this),
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        // Access To Companies Tab
        {
          label: this.translateService.instant(
            'editor-user.label.access-to-companies',
          ),
          key: 'access-to-companies',
          icon: ['fas', 'building'],
          userAccessLevel: UserAccessLevel.GLOBAL_ADMIN,
          panels: [
            {
              panelName: 'Access to Companies Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'companies',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.access-to-companies',
                  ),
                  controlType: 'tag-input',
                  additionalConfig: {
                    valueOptionsAsync: this.userCompaniesSuggestions(),
                    displayBy: 'text',
                    dropdownDisplay: 'text',
                    placeholder: 'Select companies',
                    disabled:
                      this.currentUser?.feature_components[
                        AppFeatures.COMPANY_MANAGER_ROOT
                      ] !== true &&
                      this.currentUser?.feature_components[
                        AppFeatures.GLOBAL_COMPANY_MANAGER_ROOT
                      ] !== true,
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },

        {
          label: this.translateService.instant(
            'editor-user.label.activity-logs',
          ),
          key: 'activity-logs',
          icon: ['fas', 'history'],
          availableInFormModes: [FormMode.Edit],
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          panels: [
            {
              panelName: 'Activity Logs Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'component2',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserActivityLogsComponent,
                    inputProviderAsync: this.getUserIdInputProvider.bind(this),
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'activity',
        },
        {
          label: this.translateService.instant('editor-user.label.search-logs'),
          key: 'search-logs',
          icon: ['fas', 'search'],
          availableInFormModes: [FormMode.Edit],
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          panels: [
            {
              panelName: 'Search Logs Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'component2',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserSearchLogsComponent,
                    inputProviderAsync: this.getUserIdInputProvider.bind(this),
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'search-logs',
        },

        {
          label: this.translateService.instant('editor-user.label.output-logs'),
          key: 'output-logs',
          icon: ['fas', 'file-export'],
          availableInFormModes: [FormMode.Edit],
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          panels: [
            {
              panelName: 'Search Logs Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'component2',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserOutputLogsComponent,
                    inputProviderAsync: this.getUserIdInputProvider.bind(this),
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'search-logs',
        },

        {
          label: this.translateService.instant(
            'editor-user.label.linked-accounts',
          ),
          key: 'linked-accounts',
          icon: ['fas', 'link'],
          availableInFormModes: [FormMode.Edit],
          panels: [
            {
              panelName: 'Linked Accounts Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'component3',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserLinkedAccountsComponent,
                    inputProviderAsync: this.getUserIdInputProvider.bind(this),
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'linked-accounts',
        },
        {
          label: this.translateService.instant('editor-user.label.audit-trail'),
          key: 'audit-trail',
          icon: ['fas', 'lock'],
          availableInFormModes: [FormMode.Edit],
          userAccessLevel: UserAccessLevel.CLIENT_ADMIN,
          panels: [
            {
              panelName: 'Audit Trail Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'audit-trail',
                  propertyLabel: this.translateService.instant(
                    'editor-user.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: AuditTrailComponent,
                    inputs: {
                      object: this.user,
                      hasOwner: false,
                      timeCreatedKey: 'time_created.absolute',
                      timeModifiedKey: 'time_modified.absolute',
                    },
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'audit-trail',
        },
      ],
    };
  }
}
